import {
  DEFAULT_ALERT_WINDOW_MINUTES,
  fetchLotPhotos,
  fetchMyWatchlist,
  isLotStillOpen,
  setWatched,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, GuestGate, LotCard, type LotCardLot } from '../../components/bidder-web';
import { LiveStatusBanner, mostUrgentStatus } from '../../components/bidder-web/LiveStatusBanner';
import { SaleAside } from '../../components/bidder-web/SaleAside';
import { useLiveEverywhereAside } from '../../components/bidder-web/useLiveSaleAside';
import { useConnection } from '../../hooks/useConnection';
import { liveRowFrom, saleGroupsFor, useSaleFeeds } from '../../hooks/useSaleFeeds';
import { useServerClock } from '../../hooks/useServerClock';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type WatchedLot = LotCardLot & { auction_id: string; status: string };
type SaleRef = Pick<Tables<'auctions'>, 'id' | 'slug' | 'status' | 'timezone'>;

/**
 * The watchlist.
 *
 * NEW PAGE (web design handoff, 2026-09-02) — it is one of the nav's six
 * destinations, and it had none. Watched lots were a section buried in the
 * dashboard, below bids and invoices, which is the wrong place for the list a
 * bidder opens to decide what to bid on next.
 *
 * 🔴 A WATCHLIST NEEDS AN ACCOUNT. Jim settled this on 2026-09-03 and the
 * reasoning is one line: a watchlist belongs to somebody, so there is nothing
 * for a signed-out visitor to have a list of. Guests get the gate, not an empty
 * grid — an empty grid would read as "you are watching nothing", which is a
 * claim we cannot make about a person we have not identified.
 *
 * ORDER IS THE ORDER THEY WERE BOOKMARKED, newest first, and that is imposed by
 * `fetchMyWatchlist` rather than left to the database — `.in()` returns rows in
 * whatever order it likes, and a list that reshuffles itself between visits for
 * no reason a bidder can see is a list they stop trusting.
 *
 * 🔴 LIVE (2026-10-08). Every watched lot updates without a refresh — price,
 * count, clock, and the move from "Still open" to "Closed" when its hammer
 * falls. One connection per sale that still has an open lot here
 * (`useSaleFeeds`): the engine's feed told which lots are watched, or Realtime
 * for a sale on the Supabase book. The figures are the shared rule's
 * (`withLiveFigures`).
 */
export function PublicWatchlist() {
  const { session, loading: sessionLoading, userId } = useSession();
  const { nowServerMs } = useServerClock();
  const { state: connection } = useConnection();

  const [lots, setLots] = useState<WatchedLot[]>([]);
  const [sales, setSales] = useState<ReadonlyMap<string, SaleRef>>(new Map());
  const [photos, setPhotos] = useState<ReadonlyMap<string, { url: string }[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(0);

  /**
   * 🔴 SCOPED TO EVERY SALE, and that is the whole question this page raised.
   *
   * Jim, 2026-09-18: *"Shouldn't it describe all of the sales that the buyer is
   * involved in?"* — yes. Home and the lot page are about one sale and pass its
   * id. A watchlist can hold lots from three auctions at once, so "your standing
   * across this sale" has no single answer here; scoping it to one would put a
   * number on screen that describes a fraction of the list underneath it.
   *
   * `'everywhere'` also changes the words, not just the figures — see
   * `SaleAside`'s `scope`. LIVE since 2026-10-11 (`useLiveEverywhereAside`,
   * below the connections it listens to).
   */

  useEffect(() => {
    if (userId === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetchMyWatchlist(supabase, userId);
        if (cancelled) return;
        const rows = res.lots as unknown as WatchedLot[];
        setLots(rows);
        // `missing` counts watched lots that are no longer publicly readable —
        // withdrawn, or their sale unpublished. Saying so beats quietly showing
        // a shorter list than the bidder remembers bookmarking.
        setMissing(res.missing);

        const auctionIds = [...new Set(rows.map((l) => l.auction_id))];
        const [{ data: sales }, photoMap] = await Promise.all([
          auctionIds.length === 0
            ? Promise.resolve({ data: [] })
            : supabase.from('auctions').select('id, slug, status, timezone').in('id', auctionIds),
          fetchLotPhotos(supabase, rows),
        ]);
        if (cancelled) return;
        setSales(new Map(((sales ?? []) as SaleRef[]).map((s) => [s.id, s])));
        setPhotos(photoMap);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Could not load your watchlist.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ── live ──
  const saleStatus = useMemo(
    () => new Map([...sales].map(([id, s]) => [id, s.status] as const)),
    [sales],
  );
  // Every lot here is watched, so each sale's feed is told about all its open ones.
  const groups = useMemo(
    () => saleGroupsFor(lots, new Set(lots.map((l) => l.id)), saleStatus),
    [lots, saleStatus],
  );
  const live = useSaleFeeds(groups);
  const lotNumbers = useMemo(() => new Map(lots.map((l) => [l.id, l.lot_number])), [lots]);
  const { standing, events } = useLiveEverywhereAside({ live, lotNumbers });
  const liveLots = useMemo(() => lots.map((l) => liveRowFrom(l, live)), [lots, live]);
  const urgent = mostUrgentStatus(live.statuses());

  const unwatch = useCallback(
    (lotId: string) => {
      if (userId === null) return;
      // Removed from the list immediately. This page IS the watchlist, so a lot
      // that stayed on screen with an empty heart would be the page arguing
      // with itself about what it contains.
      setLots((prev) => prev.filter((l) => l.id !== lotId));
      void setWatched(supabase, userId, lotId, false);
    },
    [userId],
  );

  if (sessionLoading) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <GuestGate
          title="Watching a lot needs an account"
          body="A watchlist belongs to somebody, so there is nothing to show until we know who you are. Browsing needs no account at all."
        />
      </div>
    );
  }

  const openLots = liveLots.filter((l) => isLotStillOpen(l));
  const closedLots = liveLots.filter((l) => !isLotStillOpen(l));

  return (
    <div className="w-wrap">
      {/* A sale whose live updates are down says so — the most urgent one, in
          that sale's own time zone. */}
      <LiveStatusBanner
        status={urgent?.info ?? null}
        timeZone={urgent === null ? null : (sales.get(urgent.saleId)?.timezone ?? null)}
      />
      {/* 🔴 `.lr-top` IS THE TWO-COLUMN BAND, and using it is the whole point.
          Dropped into normal flow the panels went full-width UNDERNEATH the
          heading; on every other page they sit BESIDE it. Jim, 2026-09-18:
          *"The tables should still be on the right, not underneath."*

          This is the same grid the Live Room's header uses —
          `minmax(0,1fr) minmax(320px,660px)` — so the two pages cannot drift on
          where the panels sit, and the existing container query at 1300px drops
          them below on narrow screens for both at once. */}
      <div className="lr-top">
        <div style={{ paddingTop: 40, paddingBottom: 26 }}>
          <h1 className="w-h1">Watchlist</h1>
          <p className="w-body muted" style={{ marginTop: 10, maxWidth: 560 }}>
            Lots you are keeping an eye on, newest first. Watching does not bid — set a maximum on
            the lot when you are ready. Anything you bid on is added here automatically.
          </p>
          {/* The closing reminder is a WATCHLIST fact, so it is stated on the
              watchlist. It used to sit in the Search rail as three chips that
              displayed the default and could not be clicked — a dead control,
              for a different feature, on a page where the bidder has not chosen
              a lot yet and so has nothing whose close could be announced. */}
          <p className="w-body muted" style={{ marginTop: 8, maxWidth: 560, fontSize: 13.5 }}>
            Lots here get a reminder {DEFAULT_ALERT_WINDOW_MINUTES} minutes before they close. To
            choose a different window for one lot, set it on that lot&apos;s page. Outbid alerts are
            always on and are not affected by this.
          </p>
        </div>

        {/* The same two panels Home, the lot page and the Live Room carry — Jim,
            2026-09-18: *"I want both of those tables in the headers on Home,
            Lot, and Watchlist on the website."*

            🔴 SCOPED TO EVERY SALE HERE, AND ONLY HERE. The list below spans
            auctions, so a standing scoped to one of them would describe a
            fraction of what the bidder is looking at. `scope="everywhere"` also
            changes the wording, so the panel never claims to be about "this
            sale" on the one page that has no single sale.

            `offline` is the shared connection state, same as the lot cards use,
            so the feed stops rather than showing old bids as current. */}
        <SaleAside
          standing={standing}
          events={events}
          offline={connection === 'offline'}
          nowServerMs={nowServerMs}
          scope="everywhere"
        />
      </div>

      {/* Derived from the lots on screen, not from the fetch, so a lot that
          closes while the page is open moves section on the next render — and
          so the phone and the website split the list by the same rule. */}
      {loading ? (
        <p className="muted">Loading your watchlist…</p>
      ) : error !== null && lots.length === 0 ? (
        <EmptyState icon="!" title="Your watchlist could not be loaded" body={error} />
      ) : lots.length === 0 ? (
        <EmptyState
          icon="♡"
          title="Nothing on your watchlist yet"
          body="Tap the heart on any lot and it appears here, with its price and clock."
          action={
            <Link to="/browse" className="w-btn w-btn-cta">
              Browse the lots
            </Link>
          }
        />
      ) : (
        <>
          {missing > 0 ? (
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
              {missing} lot{missing === 1 ? '' : 's'} you were watching{' '}
              {missing === 1 ? 'is' : 'are'} no longer listed — withdrawn, or in a sale that is no
              longer public.
            </p>
          ) : null}
          {/* Two sections, because they answer different questions. Open is the
              working list — what can I still act on. Closed is the record of how
              the sale is going for me, and it only exists while the sale is
              still running: a lot leaves the watchlist entirely once its SALE
              ends, which is the reader's rule, not this page's. */}
          {(
            [
              ['Still open', openLots],
              ['Closed', closedLots],
            ] as [string, typeof liveLots][]
          )
            .filter(([, rows]) => rows.length > 0)
            .map(([heading, rows]) => (
              <section key={heading} style={{ paddingBottom: 12 }}>
                {/* The heading is only worth drawing when there is a second
                    section to tell it apart from. */}
                {openLots.length > 0 && closedLots.length > 0 ? (
                  <h2 className="w-h2" style={{ fontSize: 19, margin: '10px 0 14px' }}>
                    {heading}
                    <span
                      className="muted"
                      style={{ fontSize: 14, fontWeight: 500, marginLeft: 9 }}
                    >
                      {rows.length}
                    </span>
                  </h2>
                ) : null}
                <div className="w-grid">
                  {rows.map((lot) => (
                    <LotCard
                      key={lot.id}
                      lot={lot}
                      auctionSlug={sales.get(lot.auction_id)?.slug ?? ''}
                      photoUrl={photos.get(lot.id)?.[0]?.url ?? null}
                      nowServerMs={nowServerMs}
                      offline={connection === 'offline'}
                      watched
                      onToggleWatch={unwatch}
                    />
                  ))}
                </div>
              </section>
            ))}
          <div style={{ paddingBottom: 40 }} />
        </>
      )}
    </div>
  );
}
